import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { confirmDelete } from '../../utils/confirm';

function RoleModal({ tenantId, role, onClose, onSaved }) {
  const isEdit = !!role;
  const [form, setForm] = useState({
    roleName: role?.RoleName || '',
    description: role?.Description || '',
    segmentId: role?.SegmentID || '',
  });
  const [segments, setSegments] = useState([]);
  const [menus, setMenus] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [segRes, menuRes] = await Promise.all([
          api.get('/roles/segments'),
          api.get('/roles/menus'),
        ]);
        if (segRes.data.success) setSegments(segRes.data.data);
        if (menuRes.data.success) {
          setMenus(menuRes.data.data);
          // Initialize permissions
          const perms = {};
          menuRes.data.data.forEach((m) => {
            perms[m.MenuID] = { canRead: false, canWrite: false, canDelete: false };
          });
          // Load existing permissions if editing
          if (isEdit) {
            const detailRes = await api.get(`/roles/detail/${role.RoleID}`);
            if (detailRes.data.success && detailRes.data.data.permissions) {
              detailRes.data.data.permissions.forEach((p) => {
                perms[p.MenuID] = { canRead: p.CanRead, canWrite: p.CanWrite, canDelete: p.CanDelete };
              });
              if (detailRes.data.data.SegmentID) {
                setForm(f => ({ ...f, segmentId: detailRes.data.data.SegmentID }));
              }
            }
          }
          setPermissions(perms);
        }
      } catch {
        toast.error('Failed to load data');
      }
    }
    load();
  }, [isEdit, role]);

  const togglePerm = (menuId, field) => {
    setPermissions((prev) => ({
      ...prev,
      [menuId]: { ...prev[menuId], [field]: !prev[menuId][field] },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.roleName.trim()) {
      toast.error('Role Name is required');
      return;
    }

    const permArray = Object.entries(permissions).map(([menuId, perm]) => ({
      menuId: parseInt(menuId),
      ...perm,
    }));

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/roles/${role.RoleID}`, { ...form, permissions: permArray });
        toast.success('Role updated');
      } else {
        await api.post('/roles', { tenantId, ...form, permissions: permArray });
        toast.success('Role created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit Role' : 'Create New Role'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="required-label">Role Name</label>
            <input type="text" placeholder="e.g., Paper Setter, Evaluator" value={form.roleName} onChange={(e) => setForm({ ...form, roleName: e.target.value })} autoFocus />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea placeholder="Brief description of this role" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="form-textarea" />
          </div>
          <div className="form-group">
            <label>Segment</label>
            <select value={form.segmentId} onChange={(e) => setForm({ ...form, segmentId: e.target.value ? parseInt(e.target.value) : '' })} className="form-select">
              <option value="">-- Select Segment --</option>
              {segments.map((s) => (
                <option key={s.SegmentID} value={s.SegmentID}>{s.SegmentName}</option>
              ))}
            </select>
          </div>

          <div className="perm-section-header">
            <h3>Page Access Permissions</h3>
          </div>

          <div className="perm-grid">
            {menus.map((menu) => (
              <div key={menu.MenuID} className="perm-card">
                <div className="perm-card-title">
                  <span className="perm-icon">{menu.Icon}</span>
                  <strong>{menu.MenuName}</strong>
                </div>
                <div className="perm-checks">
                  <label className="perm-check-label">
                    <input type="checkbox" checked={permissions[menu.MenuID]?.canRead || false} onChange={() => togglePerm(menu.MenuID, 'canRead')} />
                    Read
                  </label>
                  <label className="perm-check-label">
                    <input type="checkbox" checked={permissions[menu.MenuID]?.canWrite || false} onChange={() => togglePerm(menu.MenuID, 'canWrite')} />
                    Write
                  </label>
                  <label className="perm-check-label">
                    <input type="checkbox" checked={permissions[menu.MenuID]?.canDelete || false} onChange={() => togglePerm(menu.MenuID, 'canDelete')} />
                    Delete
                  </label>
                </div>
              </div>
            ))}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-save" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RolesTab({ tenantId }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editRole, setEditRole] = useState(null);

  const loadRoles = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const response = await api.get(`/roles/${tenantId}`);
      if (response.data.success) setRoles(response.data.data);
    } catch {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  const handleDelete = async (roleId) => {
    if (!(await confirmDelete('Role'))) return;
    try {
      await api.delete(`/roles/${roleId}`);
      toast.success('Role deleted');
      loadRoles();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  return (
    <>
      <div className="page-card-header" style={{ margin: 0, paddingBottom: 16, borderBottom: '1px solid #e0e0e0' }}>
        <h3>Role Management</h3>
        <button className="btn btn-add" onClick={() => { setEditRole(null); setShowModal(true); }}>
          + Create Role
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : roles.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>No roles created. Click "Create Role" to add one.</div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Role Name</th>
              <th>Description</th>
              <th>Segment</th>
              <th>Permissions</th>
              <th>Users</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.RoleID}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="role-shield">🛡</span>
                    <strong>{r.RoleName}</strong>
                  </div>
                </td>
                <td style={{ color: '#616161' }}>{r.Description || '-'}</td>
                <td>{r.SegmentName ? <span className="badge badge-provider">{r.SegmentName}</span> : '-'}</td>
                <td><span style={{ color: '#00796b' }}>{r.PermissionCount} pages accessible</span></td>
                <td>
                  <span className="user-count-badge">{r.UserCount}</span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn action-edit" title="Edit" onClick={() => { setEditRole(r); setShowModal(true); }}>✏</button>
                    {!r.IsSystem && (
                      <button className="action-btn action-delete" title="Delete" onClick={() => handleDelete(r.RoleID)}>🗑</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <RoleModal
          tenantId={tenantId}
          role={editRole}
          onClose={() => { setShowModal(false); setEditRole(null); }}
          onSaved={() => { setShowModal(false); setEditRole(null); loadRoles(); }}
        />
      )}
    </>
  );
}

export default RolesTab;
